import { create } from 'zustand';
import { persist, PersistStorage } from 'zustand/middleware';
import type { User } from '../types/auth';

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setAuth: (user: User, token: string, refreshToken: string) => void;
  setToken: (token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  hydrate: () => void;
}

// Storage interface that supports both sync and async operations
interface PlatformStorage {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => void | Promise<void>;
  removeItem: (name: string) => void | Promise<void>;
}

// Storage that will be set by the platform (web/mobile)
let platformStorage: PlatformStorage | null = null;

export const setAuthStorage = (customStorage: PlatformStorage) => {
  platformStorage = customStorage;
  // Trigger rehydration after storage is set
  useAuthStore.persist.rehydrate();
};

// Custom storage wrapper that delegates to platformStorage and handles async
const createPlatformStorage = (): PersistStorage<Partial<AuthState>> => ({
  getItem: async (name) => {
    if (!platformStorage) return null;
    try {
      const str = await Promise.resolve(platformStorage.getItem(name));
      if (!str) return null;
      return JSON.parse(str);
    } catch {
      return null;
    }
  },
  setItem: async (name, value) => {
    if (!platformStorage) return;
    try {
      await Promise.resolve(platformStorage.setItem(name, JSON.stringify(value)));
    } catch (e) {
      console.warn('Failed to save auth state:', e);
    }
  },
  removeItem: async (name) => {
    if (!platformStorage) return;
    try {
      await Promise.resolve(platformStorage.removeItem(name));
    } catch (e) {
      console.warn('Failed to remove auth state:', e);
    }
  },
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setAuth: (user, token, refreshToken) =>
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      setToken: (token) =>
        set({
          token,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setLoading: (loading) => set({ isLoading: loading }),

      hydrate: () => {
        // Manually trigger rehydration from storage
        useAuthStore.persist.rehydrate();
      },
    }),
    {
      name: 'tradermind-auth',
      storage: createPlatformStorage(),
      skipHydration: true, // Skip automatic hydration, we'll do it manually after storage is set
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state, error) => {
        // After rehydration, update isAuthenticated based on loaded state
        if (error) {
          console.warn('Auth rehydration error:', error);
          useAuthStore.setState({ isLoading: false });
          return;
        }
        if (state) {
          const hasAuth = !!(state.token && state.user);
          // Must use setState to trigger re-render, direct mutation doesn't work
          useAuthStore.setState({ 
            isAuthenticated: hasAuth, 
            isLoading: false 
          });
        } else {
          // No state loaded, just set loading to false
          useAuthStore.setState({ isLoading: false });
        }
      },
    }
  )
);

export const getToken = (): string | null => {
  return useAuthStore.getState().token;
};

export const getRefreshToken = (): string | null => {
  return useAuthStore.getState().refreshToken;
};
